import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { BarChart3, Globe, Shield, Sparkles, TrendingUp, Users } from 'lucide-react';

const CONSULT_SMS_NUMBERS = ['+233200738204', '+233256895006'];

const sendConsultSMS = async (phoneNumber, message) => {
  const hubtelEndpoint = 'https://smsc.hubtel.com/v1/messages/send';
  const clientId = process.env.REACT_APP_HUBTEL_CLIENT_ID || 'vxojxzbs';
  const clientSecret = process.env.REACT_APP_HUBTEL_CLIENT_SECRET || 'uznaitfd';
  try {
    const credentials = btoa(`${clientId}:${clientSecret}`);
    const response = await fetch(hubtelEndpoint, {
      method: 'POST',
      headers: {
        Authorization: `Basic ${credentials}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        From: 'AFB',
        To: phoneNumber.replace(/\s/g, ''),
        Content: message
      })
    });
    return response.ok;
  } catch (err) {
    console.error('SMS error:', err);
    return false;
  }
};

const services = [
  {
    title: 'Investment banking',
    icon: TrendingUp,
    description: 'Clear capital strategy for ambitious growth and disciplined execution.'
  },
  {
    title: 'Asset management',
    icon: BarChart3,
    description: 'Thoughtful portfolio positioning with a calm focus on long-term value.'
  },
  {
    title: 'Corporate finance',
    icon: Shield,
    description: 'Practical financial planning, structuring, and operational support.'
  },
  {
    title: 'Financial inclusion',
    icon: Users,
    description: 'Programs designed to expand access and strengthen community development.'
  },
  {
    title: 'Food security',
    icon: Globe,
    description: 'Support for sustainable food systems and resilient local economies.'
  },
  {
    title: 'Infrastructure',
    icon: Sparkles,
    description: 'Long-term support for development that powers growth and opportunity.'
  }
];

const process = [
  ['01', 'Listen closely', 'We begin by understanding your priorities, timing, and constraints.'],
  ['02', 'Shape a path', 'We turn your goals into a thoughtful strategy with clear options.'],
  ['03', 'Deliver with care', 'We stay engaged through execution and help keep momentum steady.']
];

const Services = () => {
  const [form, setForm] = useState({ name: '', email: '', service: '', message: '' });
  const [status, setStatus] = useState('');
  const [isSending, setIsSending] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus('');
    setIsSending(true);
    const smsContent = `AFB Consultation: ${form.name}. Email: ${form.email}. Service: ${form.service}. Message: ${form.message}`;
    try {
      const results = await Promise.all(CONSULT_SMS_NUMBERS.map((number) => sendConsultSMS(number, smsContent)));
      if (results.every(Boolean)) {
        setStatus('Thanks. We will reach out shortly.');
        setForm({ name: '', email: '', service: '', message: '' });
      } else {
        setStatus('Your request was received, and we will still follow up directly.');
      }
    } catch (error) {
      setStatus('Please try again or contact us directly.');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fbff] pt-24">
      <section className="px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl rounded-[2rem] border border-blue-100 bg-white p-8 shadow-sm sm:p-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-blue-600">Services</p>
            <h1 className="mt-4 text-4xl font-semibold text-slate-900 sm:text-5xl">Minimal support for ambitious financial goals.</h1>
            <p className="mt-6 text-lg leading-8 text-slate-600">We provide focused advisory and delivery across capital, growth, development, and impact-focused initiatives.</p>
          </motion.div>

          <div className="mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {services.map((service, index) => (
              <motion.div key={service.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: index * 0.06 }} className="rounded-2xl border border-blue-100 bg-blue-50/70 p-7">
                <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-xl bg-blue-600 text-white">
                  <service.icon className="h-5 w-5" />
                </div>
                <h2 className="text-xl font-semibold text-slate-900">{service.title}</h2>
                <p className="mt-3 text-base leading-7 text-slate-600">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 pb-20 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[1.05fr_0.95fr]">
          <div className="rounded-[2rem] border border-blue-100 bg-white p-8 shadow-sm sm:p-10">
            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-blue-600">How we work</p>
            <h2 className="mt-3 text-3xl font-semibold text-slate-900 sm:text-4xl">A clear process from first conversation to implementation.</h2>
            <div className="mt-8 space-y-4">
              {process.map(([step, title, description]) => (
                <div key={step} className="rounded-2xl border border-blue-100 bg-blue-50/70 p-5">
                  <p className="text-sm font-semibold uppercase tracking-[0.25em] text-blue-600">{step}</p>
                  <h3 className="mt-2 text-lg font-semibold text-slate-900">{title}</h3>
                  <p className="mt-2 text-base leading-7 text-slate-600">{description}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-[2rem] border border-blue-100 bg-white p-8 shadow-sm sm:p-10">
            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-blue-600">Book a consultation</p>
            <h2 className="mt-3 text-3xl font-semibold text-slate-900 sm:text-4xl">Tell us what you are planning.</h2>
            <form onSubmit={handleSubmit} className="mt-8 space-y-4">
              <input className="w-full rounded-xl border border-blue-100 bg-blue-50/70 px-4 py-3 outline-none ring-0" placeholder="Your name" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} required />
              <input type="email" className="w-full rounded-xl border border-blue-100 bg-blue-50/70 px-4 py-3 outline-none ring-0" placeholder="Email address" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} required />
              <input className="w-full rounded-xl border border-blue-100 bg-blue-50/70 px-4 py-3 outline-none ring-0" placeholder="Service interest" value={form.service} onChange={(event) => setForm({ ...form, service: event.target.value })} required />
              <textarea rows="4" className="w-full rounded-xl border border-blue-100 bg-blue-50/70 px-4 py-3 outline-none ring-0" placeholder="Briefly describe your needs" value={form.message} onChange={(event) => setForm({ ...form, message: event.target.value })} required />
              <button type="submit" disabled={isSending} className="w-full rounded-xl bg-blue-600 px-4 py-3 font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-70">{isSending ? 'Sending…' : 'Request consultation'}</button>
              {status ? <p className="text-sm text-slate-600">{status}</p> : null}
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
